import React, { useState, useEffect } from "react";
import api from "./axiosConfig";
import { useNavigate } from "react-router-dom";
import Icon from "./components/Icon";
import showIcon from "./assets/icons/visibility.svg";
import hideIcon from "./assets/icons/visibility-off.svg";
import "./LoginPage.css";

const PUBLIC_KEY = `-----BEGIN PUBLIC KEY-----
MIIBIjANBgkqhkiG9w0BAQEFAAOCAQ8AMIIBCgKCAQEAne/6S5L52q4KXeCy3gqo
cCuPiZzVWjg9v8J7f8dbHhWuO/WsWJfc2AC7Y82lgKK19gg9wYN4h4r+J7mNpvjI
3YoTxm215O628iSzqWdBKTaBif2pU6Ksp7dcvMQk5sKQzuy7dXEBY+j56cDznnnI
41RAHnOyEIO+5Nz2oDGUfRMyIjAGI4eOQqz75oD4ZpESbPXpUr0RamtnLECzp1TV
eLtuqds8qQw2zwipq0lwntHq2gPKtNxPbuNPfRqHcewDm4qebJO0yQBy+kvcaNGR
B9K5JFThER6PlWx8Ng2Aov4MW6AtUwCyw4YghWO4NPXQtXsh3hPpqAn263/9RjnT
rwIDAQAB
-----END PUBLIC KEY-----`;

const importPublicKey = async (publicKeyBase64) => {
  const binaryDer = Uint8Array.from(atob(publicKeyBase64), (c) => c.charCodeAt(0));

  return await window.crypto.subtle.importKey(
    "spki",
    binaryDer,
    {
      name: "RSA-OAEP",
      hash: "SHA-256",
    },
    true,
    ["encrypt"]
  );
};

function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [token, setToken] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Check if password field should be shown (for admin/superadmin)
  const isAdminLogin = username.toLowerCase() === "superadmin" || username.toLowerCase() === "admin";
  
  const navigate = useNavigate();

  useEffect(() => {
      localStorage.setItem("accessToken", token);
  }, [token]);

  const handleLogin = async (e) => {
    console.log()
    e.preventDefault();
    setLoading(true);
    setError("");

    // CURRENT BEHAVIOR: Admin requires password, users don't
    // For admin/superadmin login
    if (isAdminLogin) {
      if (!password) {
        setError("Password is required for admin login");
        setLoading(false);
        return;
      }

      const publicKeyBase64 = PUBLIC_KEY.replace(/-----(BEGIN|END) PUBLIC KEY-----/g, "").replace(/\n/g, "");

      try {
        const publicKey = await importPublicKey(publicKeyBase64);
        const encoder = new TextEncoder();
        const encodedPassword = encoder.encode(password);

        const encryptedPassword = await window.crypto.subtle.encrypt(
          { name: "RSA-OAEP" },
          publicKey,
          encodedPassword
        );

        const encryptedPasswordBase64 = btoa(
          String.fromCharCode(...new Uint8Array(encryptedPassword))
        );

        const response = await api.post("/auth/login", {
          username,
          password: encryptedPasswordBase64,
        });

        setToken(response.data.accessToken);
        
        if (response.data.role === "superadmin" || response.data.role === "admin") {
          navigate("/admin");
        } 
        
        console.log("Admin login successful");
      } catch (err) {
        console.log("Admin login failed:", err);
        setError("Invalid admin credentials. Please try again.");
        setLoading(false);
      }
    } else {
      // For regular user login (no password required in current behavior)
      try {
        const response = await api.post("/auth/login", {
          username
        });

        setToken(response.data.accessToken);
        navigate("/user");
      } catch (err) {
        setError("User not found. Please check your username.");
        setLoading(false);
      }
    }

    /* ============================================================
     * COMMENTED OUT: ALTERNATIVE BEHAVIOR - ALL USERS NEED PASSWORD
     * ============================================================
     * Uncomment this section if you want BOTH admin and regular users
     * to require password authentication. This provides better security.
     * 
     * To enable this:
     * 1. Comment out the entire if/else block above (lines 164-210)
     * 2. Uncomment the code block below
     * 3. Update your backend to handle password for all user types
     * ============================================================
     */
    
    /*
    // ALL USERS REQUIRE PASSWORD VERSION
    if (!password) {
      setError("Password is required");
      setLoading(false);
      return;
    }

    const publicKeyBase64 = PUBLIC_KEY.replace(/-----(BEGIN|END) PUBLIC KEY-----/g, "").replace(/\n/g, "");

    try {
      const publicKey = await importPublicKey(publicKeyBase64);
      const encoder = new TextEncoder();
      const encodedPassword = encoder.encode(password);

      const encryptedPassword = await window.crypto.subtle.encrypt(
        { name: "RSA-OAEP" },
        publicKey,
        encodedPassword
      );

      const encryptedPasswordBase64 = btoa(
        String.fromCharCode(...new Uint8Array(encryptedPassword))
      );

      const response = await api.post("/auth/login", {
        username,
        password: encryptedPasswordBase64,
      });

      setToken(response.data.accessToken);
      
      if (response.data.role === "superadmin" || response.data.role === "admin") {
        navigate("/admin");
      } else {
        navigate("/user");
      }
      
      console.log("Login successful");
    } catch (err) {
      console.log("Login failed:", err);
      setError("Invalid credentials. Please try again.");
      setLoading(false);
    }
    */
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
     <div className="login-page">
      <div className="login-wrapper">
        <div className="login-header">
          <h1 className="login-title">Welcome Back</h1>
        </div>

        {/* ADD FORM TAG HERE */}
        <form className="login-form" onSubmit={handleLogin}>
          <div className="form-group">
            <label htmlFor="username" className="label label-required">
              Username
            </label>
            <input
              id="username"
              type="text"
              placeholder="Enter your username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="login-input"
              autoComplete="username"
            />
          </div>

          {isAdminLogin && (
            <div className="form-group fade-in">
              <label htmlFor="password" className="label label-required">
                Password
              </label>
              <div className="input-wrapper">
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="login-input"
                  autoComplete="current-password"
                />
                {password && (
                  <button
                    type="button"  // IMPORTANT: type="button" to prevent submission
                    onClick={togglePasswordVisibility}
                    className="password-toggle-btn"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    <Icon 
                      src={showPassword ? hideIcon : showIcon} 
                      alt="Toggle password visibility" 
                    />
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ========================================
           * COMMENTED OUT: PASSWORD FIELD FOR ALL USERS
           * ========================================
           * Uncomment this section and comment out the conditional
           * password field above if you want ALL users to enter passwords
           * ========================================
           */}
          {/*
          <div className="form-group">
            <label htmlFor="password" className="label label-required">
              Password
            </label>
            <div className="input-wrapper">
              <input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="login-input"
                autoComplete="current-password"
              />
              {password && (
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="password-toggle-btn"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  <Icon 
                    src={showPassword ? hideIcon : showIcon} 
                    alt="Toggle password visibility" 
                  />
                </button>
              )}
            </div>
          </div>
          */}

          <button
            type="submit"  // Changed from onClick to type="submit"
            disabled={loading}
            className="login-button"
          >
            {loading ? "Logging in..." : "Login"}
          </button>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}
        </form>  {/* CLOSE FORM TAG HERE */}
      </div>
    </div>
  );
}

export default LoginPage;